(() => {
  const FAQ_HEADING = /^(?:frequently asked questions?|modular sofa faq)$/i;

  const normalizeText = (value) => (value || "").replace(/\s+/g, " ").trim();

  const createSlug = (text, fallbackIndex) => {
    const slug = normalizeText(text)
      .normalize("NFKD")
      .toLowerCase()
      .replace(/[’']/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 72);

    return slug || `article-section-${fallbackIndex + 1}`;
  };

  const ensureUniqueId = (heading, index, usedIds) => {
    let base = heading.id || createSlug(heading.textContent, index);
    let candidate = base;
    let suffix = 2;

    while (usedIds.has(candidate) || (document.getElementById(candidate) && document.getElementById(candidate) !== heading)) {
      candidate = `${base}-${suffix}`;
      suffix += 1;
    }

    heading.id = candidate;
    usedIds.add(candidate);
    return candidate;
  };

  const upgradeLegacyQuickAnswers = (body) => {
    body.querySelectorAll("aside").forEach((aside) => {
      const text = normalizeText(aside.textContent).slice(0, 120);
      if (/quick answer/i.test(text)) aside.classList.add("mv-quick-answer");
    });
  };

  const upgradeTables = (body) => {
    body.querySelectorAll("table").forEach((table) => {
      const parent = table.parentElement;
      if (parent && parent.children.length === 1 && parent.tagName === "DIV") {
        parent.classList.add("mv-table-wrap");
        parent.setAttribute("role", "region");
        parent.setAttribute("aria-label", "Scrollable comparison table");
        parent.tabIndex = 0;
        return;
      }

      const wrapper = document.createElement("div");
      wrapper.className = "mv-table-wrap";
      wrapper.setAttribute("role", "region");
      wrapper.setAttribute("aria-label", "Scrollable comparison table");
      wrapper.tabIndex = 0;
      table.before(wrapper);
      wrapper.append(table);
    });
  };

  const upgradeLegacyFaq = (body) => {
    const heading = Array.from(body.querySelectorAll(":scope > h2")).find((item) => FAQ_HEADING.test(normalizeText(item.textContent)));
    if (!heading || heading.closest(".mv-faq-section, .faq-section")) return;

    const collected = [];
    let node = heading.nextSibling;

    while (node) {
      const next = node.nextSibling;
      if (node.nodeType === Node.ELEMENT_NODE && node.tagName === "H2") break;
      collected.push(node);
      node = next;
    }

    const questions = collected.filter((item) => item.nodeType === Node.ELEMENT_NODE && item.tagName === "H3");
    if (!questions.length) return;

    const section = document.createElement("section");
    section.className = "mv-faq-section";
    heading.before(section);

    const eyebrow = document.createElement("p");
    eyebrow.className = "mv-article-eyebrow";
    eyebrow.textContent = "Common questions";
    section.append(eyebrow, heading);

    const list = document.createElement("div");
    list.className = "mv-faq-list";
    section.append(list);

    let currentDetails = null;
    collected.forEach((item) => {
      if (item.nodeType === Node.ELEMENT_NODE && item.tagName === "H3") {
        currentDetails = document.createElement("details");
        currentDetails.className = "mv-faq-item";

        const summary = document.createElement("summary");
        summary.textContent = normalizeText(item.textContent);
        currentDetails.append(summary);
        list.append(currentDetails);
        item.remove();
      } else if (currentDetails) {
        currentDetails.append(item);
      } else {
        section.insertBefore(item, list);
      }
    });
  };

  const prepareFaqItems = (root) => {
    const items = Array.from(root.querySelectorAll(".mv-faq-item, .faq-item"));
    items.forEach((item) => {
      item.classList.add("mv-faq-item");
      item.addEventListener("toggle", () => {
        if (!item.open) return;
        items.forEach((other) => {
          if (other !== item && other.open) other.removeAttribute("open");
        });
      });
    });
    return items;
  };

  const hasFaqSchema = () => {
    return Array.from(document.querySelectorAll('script[type="application/ld+json"]')).some((script) => {
      try {
        const data = JSON.parse(script.textContent);
        const nodes = Array.isArray(data) ? data : [data];
        return nodes.some((node) => node && node["@type"] === "FAQPage");
      } catch (_error) {
        return false;
      }
    });
  };

  const addFaqSchema = (root, faqItems) => {
    if (root.dataset.enableFaqSchema !== "true" || !faqItems.length || hasFaqSchema()) return;

    const mainEntity = faqItems
      .map((item) => {
        const summary = item.querySelector(":scope > summary");
        if (!summary) return null;
        const answer = Array.from(item.children)
          .filter((child) => child !== summary)
          .map((child) => normalizeText(child.textContent))
          .filter(Boolean)
          .join(" ");
        if (!answer) return null;
        return {
          "@type": "Question",
          name: normalizeText(summary.textContent),
          acceptedAnswer: { "@type": "Answer", text: answer }
        };
      })
      .filter(Boolean);

    if (!mainEntity.length) return;

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.dataset.mvFaqSchema = "true";
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@type": "FAQPage", mainEntity });
    document.head.append(script);
  };

  const buildTableOfContents = (root, body) => {
    if (root.dataset.enableToc !== "true") return [];

    const headings = Array.from(body.querySelectorAll("h2")).filter((heading) => !heading.closest(".mv-feature-card, .feature-card, .mv-inline-cta, .inline-cta"));
    if (!headings.length) return [];

    const usedIds = new Set();
    const entries = headings.map((heading, index) => ({
      heading,
      id: ensureUniqueId(heading, index, usedIds),
      label: normalizeText(heading.textContent)
    }));

    root.querySelectorAll("[data-article-toc]").forEach((nav) => {
      const fragment = document.createDocumentFragment();
      entries.forEach((entry) => {
        const link = document.createElement("a");
        link.href = `#${entry.id}`;
        link.textContent = entry.label;
        fragment.append(link);
      });
      nav.replaceChildren(fragment);
    });

    root.querySelectorAll("[data-mobile-toc], [data-desktop-toc-card]").forEach((element) => element.removeAttribute("hidden"));

    if (window.location.hash) {
      try {
        const requestedId = decodeURIComponent(window.location.hash.slice(1));
        const requestedHeading = entries.find((entry) => entry.id === requestedId);
        if (requestedHeading) {
          window.requestAnimationFrame(() => requestedHeading.heading.scrollIntoView({ block: "start" }));
        }
      } catch (_error) {
        // Ignore malformed URL fragments; the article remains fully readable.
      }
    }

    if ("IntersectionObserver" in window) {
      const links = Array.from(root.querySelectorAll("[data-article-toc] a"));
      const observer = new IntersectionObserver((observed) => {
        const visible = observed
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (!visible.length) return;
        const activeId = visible[0].target.id;
        links.forEach((link) => link.classList.toggle("is-active", link.getAttribute("href") === `#${activeId}`));
      }, { rootMargin: "-18% 0px -70% 0px", threshold: 0 });
      entries.forEach((entry) => observer.observe(entry.heading));
    }

    return entries;
  };

  const setupReadingProgress = (root, body) => {
    const progress = root.querySelector("[data-reading-progress]");
    if (!progress) return;

    let frame = 0;
    const update = () => {
      frame = 0;
      const start = body.getBoundingClientRect().top + window.scrollY;
      const available = Math.max(1, body.offsetHeight - window.innerHeight);
      const value = Math.max(0, Math.min(1, (window.scrollY - start) / available));
      progress.style.width = `${value * 100}%`;
    };
    const requestUpdate = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };

    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", requestUpdate);
    update();
  };

  const copyText = async (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const input = document.createElement("textarea");
    input.value = text;
    input.setAttribute("readonly", "");
    input.style.position = "fixed";
    input.style.opacity = "0";
    document.body.append(input);
    input.select();
    document.execCommand("copy");
    input.remove();
  };

  const setupShareButtons = (root) => {
    const toast = root.querySelector("[data-article-toast]");
    let toastTimer = 0;

    root.querySelectorAll("[data-copy-article-link]").forEach((button) => {
      button.addEventListener("click", async () => {
        const url = button.dataset.copyArticleLink || window.location.href;
        try {
          await copyText(url);
          if (toast) toast.textContent = "Article link copied";
        } catch (_error) {
          if (toast) toast.textContent = url;
        }

        if (!toast) return;
        window.clearTimeout(toastTimer);
        toast.classList.add("is-visible");
        toastTimer = window.setTimeout(() => toast.classList.remove("is-visible"), 2200);
      });
    });
  };

  const initArticle = (root) => {
    if (root.dataset.mvArticleReady === "true") return;
    root.dataset.mvArticleReady = "true";

    const body = root.querySelector("[data-article-body]");
    if (!body) return;

    upgradeLegacyQuickAnswers(body);
    upgradeTables(body);
    upgradeLegacyFaq(body);
    body.querySelectorAll("img:not([loading])").forEach((image) => { image.loading = "lazy"; });

    const faqItems = prepareFaqItems(root);
    buildTableOfContents(root, body);
    addFaqSchema(root, faqItems);
    setupReadingProgress(root, body);
    setupShareButtons(root);
  };

  const init = () => document.querySelectorAll("[data-mv-article]").forEach(initArticle);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", init);
})();
